import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NotificationBell from "../components/layout/NotificationBell";
import AthleteReminderSettings from "../components/system/AthleteReminderSettings";
import PhotoUploadControl from "../components/photos/PhotoUploadControl";
import PhotoAvatar from "../components/photos/PhotoAvatar";
import { ArrowLeft, LogOut, Star, Target, Activity, TrendingUp, Settings, Mail, User, Calendar, Users, Save, AlertTriangle, X, Check, Trophy, Building2, Link, Unlink } from "lucide-react";
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, logout, updateUser, setUser } = useUser();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', birth_date: '', gender: '', sport: '' });
  const [saving, setSaving] = useState(false);
  
  // Deletion states
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);
  const [deletionRequested, setDeletionRequested] = useState(false);
  
  // Club connect states
  const [showClubConnect, setShowClubConnect] = useState(false);
  const [connectCode, setConnectCode] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectedClub, setConnectedClub] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userId = user?.id;
        const profileUrl = userId ? `${API}/profile?user_id=${userId}` : `${API}/profile`;
        const statsUrl = userId ? `${API}/stats/overview?user_id=${userId}` : `${API}/stats/overview`;
        const [profileRes, statsRes] = await Promise.all([
          axios.get(profileUrl),
          axios.get(statsUrl)
        ]);
        setProfile(profileRes.data);
        setStats(statsRes.data);
        setEditData({
          name: profileRes.data.name || '',
          birth_date: profileRes.data.birth_date || '',
          gender: profileRes.data.gender || '',
          sport: profileRes.data.sport || ''
        });
        setDeletionRequested(profileRes.data.deletion_requested || false);
        
        // Fetch connected club info if user has club_id
        if (profileRes.data.club_id) {
          try {
            const clubRes = await axios.get(`${API}/clubs/${profileRes.data.club_id}`);
            setConnectedClub(clubRes.data);
          } catch (e) {
            console.error("Error fetching club:", e);
          }
        }
      } catch (e) {
        console.error("Error fetching profile:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user?.id]);

  const handleLogout = () => {
    logout();
    navigate("/welcome");
    toast.success("Logged out successfully");
  };

  const handleSaveProfile = async () => {
    if (!editData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile/${user?.id}`, editData);
      setProfile(res.data);
      setIsEditing(false);
      toast.success("Profile updated successfully");
      // Update user context if name changed
      if (updateUser && editData.name !== user?.name) {
        updateUser({ name: editData.name });
      }
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error("Please fill all password fields");
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/profile/${user?.id}/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success("Password changed. Please sign in again.");
      logout();
      navigate("/login");
      setShowPasswordChange(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRequestDeletion = async () => {
    try {
      await axios.post(`${API}/profile/${user?.id}/request-deletion`);
      setDeletionRequested(true);
      setProfile(current => ({ ...current, photo_url: null }));
      setUser(current => ({ ...current, photo_url: null }));
      setShowDeletionConfirm(false);
      toast.success("Account deletion requested. Chat conversations have been removed.");
    } catch (e) {
      toast.error("Failed to request deletion");
    }
  };

  const handleCancelDeletion = async () => {
    try {
      await axios.post(`${API}/profile/${user?.id}/cancel-deletion`);
      setDeletionRequested(false);
      toast.success("Deletion request cancelled");
    } catch (e) {
      toast.error("Failed to cancel deletion request");
    }
  };

  const handleClubConnect = async () => {
    if (!connectCode.trim()) {
      toast.error("Please enter a connect code");
      return;
    }
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/clubs/connect`, {
        connect_code: connectCode.toUpperCase(),
        user_id: user?.id
      });
      setConnectedClub({ id: res.data.club_id, name: res.data.club_name });
      setProfile({ ...profile, club_id: res.data.club_id });
      setShowClubConnect(false);
      setConnectCode('');
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid connect code");
    } finally {
      setConnecting(false);
    }
  };

  const handleClubDisconnect = async () => {
    try {
      await axios.post(`${API}/clubs/disconnect?user_id=${user?.id}`);
      setConnectedClub(null);
      setProfile({ ...profile, club_id: null });
      toast.success("Disconnected from club");
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  const genderOptions = [
    { value: '', label: 'Select gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const sportOptions = [
    { value: '', label: 'Select sport' },
    { value: 'football', label: 'Football' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'hockey', label: 'Hockey' },
    { value: 'rugby', label: 'Rugby' },
    { value: 'baseball', label: 'Baseball' },
    { value: 'athletics', label: 'Athletics' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'other', label: 'Other' }
  ];

  const achievements = [
    { id: 1, title: 'First Goal', description: 'Created your first training goal', icon: Target, unlocked: stats?.goals_achieved > 0 },
    { id: 2, title: 'Week Warrior', description: 'Completed 7 training sessions', icon: Activity, unlocked: stats?.total_sessions >= 7 },
    { id: 3, title: 'Consistency King', description: 'Achieved a 5-day streak', icon: TrendingUp, unlocked: stats?.longest_streak >= 5 },
    { id: 4, title: 'Goal Getter', description: 'Achieved 5 goals', icon: Star, unlocked: stats?.goals_achieved >= 5 }
  ];

  const formatGender = (gender) => {
    const option = genderOptions.find(g => g.value === gender);
    return option?.label || 'Not specified';
  };

  const formatSport = (sport) => {
    const option = sportOptions.find(s => s.value === sport);
    return option?.label || 'Not specified';
  };

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-profile">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>Profile</h1>
        <div className="flex items-center gap-2"><NotificationBell /><button data-testid="settings-button" onClick={() => setShowPasswordChange(!showPasswordChange)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <Settings className={`w-5 h-5 ${isDark ? 'text-white/70' : 'text-[#493986]/70'}`} />
        </button></div>
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : (
          <>
            {/* Deletion Warning Banner */}
            {deletionRequested && (
              <div className={`p-4 rounded-2xl ${isDark ? 'bg-red-500/20 border-red-500/30' : 'bg-red-100 border-red-200'} border mb-4`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>Deletion Requested</h4>
                    <p className={`text-sm ${isDark ? 'text-red-300/80' : 'text-red-600/80'}`}>Your account is scheduled for deletion within 30 days.</p>
                    <button onClick={handleCancelDeletion} className={`mt-2 text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-600'} underline`}>
                      Cancel deletion request
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Card */}
            {user?.account_type === 'athlete' && <AthleteReminderSettings theme={theme} />}
            <div className={`p-6 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center gap-4 mb-4">
                <PhotoAvatar photoUrl={profile?.photo_url} name={profile?.name} className="h-20 w-20 rounded-2xl text-3xl" />
                <div className="flex-1">
                  <h2 className={`text-xl font-bold ${theme.textPrimary}`}>{profile?.name}</h2>
                  <p className={`text-sm ${theme.textMuted}`}>{profile?.account_type === 'coach' ? 'Coach' : 'Athlete'}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span className={`text-sm ${theme.textPrimary}`}>{stats?.average_goal_rating?.toFixed(1) || '-'} avg</span>
                  </div>
                </div>
                {!isEditing && (
                  <button onClick={() => setIsEditing(true)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDark ? 'bg-white/10 text-white' : 'bg-[#493986]/10 text-[#493986]'}`}>
                    Edit
                  </button>
                )}
              </div>

              <div className="mb-5"><PhotoUploadControl endpoint={`/profile/${user?.id}/photo`} photoUrl={profile?.photo_url} disabled={saving || deletionRequested} onSaved={photo_url => { setProfile(current => ({ ...current, photo_url })); setUser(current => ({ ...current, photo_url })); toast.success(photo_url ? 'Profile photo saved' : 'Profile photo removed'); }} /></div>
              <div className={`grid grid-cols-3 gap-3 pt-4 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.total_sessions || 0}</p>
                  <p className={`text-xs ${theme.textMuted}`}>Sessions</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.goals_achieved || 0}</p>
                  <p className={`text-xs ${theme.textMuted}`}>Goals</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${theme.textPrimary}`}>{stats?.current_streak || 0}</p>
                  <p className={`text-xs ${theme.textMuted}`}>Streak</p>
                </div>
              </div>
            </div>

            {/* Edit Profile Form */}
            {isEditing && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${theme.textPrimary}`}>Edit Profile</h3>
                  <button onClick={() => setIsEditing(false)} className={`p-1.5 rounded-lg ${theme.button}`}>
                    <X className={`w-4 h-4 ${theme.textMuted}`} />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`text-xs font-medium ${theme.textMuted} mb-1 block`}>Name *</label>
                    <input 
                      type="text" 
                      value={editData.name} 
                      onChange={(e) => setEditData({...editData, name: e.target.value})} 
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input}`} 
                      placeholder="Your name"
                      data-testid="edit-name-input"
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${theme.textMuted} mb-1 block`}>Birth Date</label>
                    <input 
                      type="date" 
                      value={editData.birth_date} 
                      onChange={(e) => setEditData({...editData, birth_date: e.target.value})} 
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                      data-testid="edit-birthdate-input"
                    />
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${theme.textMuted} mb-1 block`}>Gender</label>
                    <select 
                      value={editData.gender} 
                      onChange={(e) => setEditData({...editData, gender: e.target.value})} 
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                      data-testid="edit-gender-select"
                    >
                      {genderOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`text-xs font-medium ${theme.textMuted} mb-1 block`}>Sport</label>
                    <select 
                      value={editData.sport} 
                      onChange={(e) => setEditData({...editData, sport: e.target.value})} 
                      className={`w-full px-4 py-3 rounded-xl border ${theme.input}`}
                      data-testid="edit-sport-select"
                    >
                      {sportOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    onClick={handleSaveProfile} 
                    disabled={saving} 
                    className="w-full py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="save-profile-button"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {/* Personal Information */}
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Personal Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className={`w-5 h-5 ${theme.textMuted}`} />
                  <div>
                    <p className={`text-xs ${theme.textMuted}`}>Email</p>
                    <span className={theme.textSecondary}>{profile?.email || 'No email'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className={`w-5 h-5 ${theme.textMuted}`} />
                  <div>
                    <p className={`text-xs ${theme.textMuted}`}>Birth Date</p>
                    <span className={theme.textSecondary}>
                      {profile?.birth_date ? new Date(profile.birth_date).toLocaleDateString() : 'Not specified'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className={`w-5 h-5 ${theme.textMuted}`} />
                  <div>
                    <p className={`text-xs ${theme.textMuted}`}>Gender</p>
                    <span className={theme.textSecondary}>{formatGender(profile?.gender)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Trophy className={`w-5 h-5 ${theme.textMuted}`} />
                  <div>
                    <p className={`text-xs ${theme.textMuted}`}>Sport</p>
                    <span className={theme.textSecondary}>{formatSport(profile?.sport)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className={`w-5 h-5 ${theme.textMuted}`} />
                  <div>
                    <p className={`text-xs ${theme.textMuted}`}>Member since</p>
                    <span className={theme.textSecondary}>
                      {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Club Connect Section - Only for athletes and coaches */}
            {profile?.account_type !== 'club' && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-semibold ${theme.textPrimary}`}>Club Connection</h3>
                  <Building2 className={`w-5 h-5 ${theme.textMuted}`} />
                </div>
                
                {connectedClub ? (
                  <div>
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'} mb-3`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${isDark ? 'bg-green-500/20' : 'bg-green-100'} flex items-center justify-center`}>
                          <Building2 className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className={`font-medium ${theme.textPrimary}`}>{connectedClub.name}</p>
                          <p className={`text-xs text-green-500`}>Connected</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleClubDisconnect}
                      className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'} font-medium`}
                    >
                      <Unlink className="w-4 h-4" />
                      Disconnect from Club
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className={`text-sm mb-3 ${theme.textMuted}`}>
                      Connect to your club to share your training data with coaches and administrators.
                    </p>
                    <button
                      onClick={() => setShowClubConnect(true)}
                      className="w-full py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl flex items-center justify-center gap-2"
                      data-testid="club-connect-button"
                    >
                      <Link className="w-4 h-4" />
                      Club Connect
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Password Change */}
            {showPasswordChange && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Change Password</h3>
                <div className="space-y-3">
                  <div>
                    <label className={`text-xs ${theme.textMuted}`}>Current Password</label>
                    <input type="password" value={passwordData.current} onChange={(e) => setPasswordData({...passwordData, current: e.target.value})} className={`w-full px-4 py-3 rounded-xl border ${theme.input}`} placeholder="Current password" />
                  </div>
                  <div>
                    <label className={`text-xs ${theme.textMuted}`}>New Password</label>
                    <input type="password" value={passwordData.new} onChange={(e) => setPasswordData({...passwordData, new: e.target.value})} className={`w-full px-4 py-3 rounded-xl border ${theme.input}`} placeholder="New password" />
                  </div>
                  <div>
                    <label className={`text-xs ${theme.textMuted}`}>Confirm New Password</label>
                    <input type="password" value={passwordData.confirm} onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})} className={`w-full px-4 py-3 rounded-xl border ${theme.input}`} placeholder="Confirm new password" />
                  </div>
                  <button onClick={handleChangePassword} disabled={changingPassword} className="w-full py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl disabled:opacity-50">
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </div>
            )}

            {/* Achievements */}
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Achievements</h3>
              <div className="grid grid-cols-2 gap-3">
                {achievements.map(achievement => {
                  const Icon = achievement.icon;
                  return (
                    <div key={achievement.id} className={`p-3 rounded-xl ${achievement.unlocked ? 'bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20' : isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <Icon className={`w-6 h-6 mb-2 ${achievement.unlocked ? 'text-yellow-500' : theme.textMuted}`} />
                      <h4 className={`font-medium text-sm ${achievement.unlocked ? theme.textPrimary : theme.textMuted}`}>{achievement.title}</h4>
                      <p className={`text-xs ${theme.textMuted}`}>{achievement.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Account Actions */}
            <div className="space-y-3">
              {/* Logout Button */}
              <button data-testid="logout-button" onClick={handleLogout} className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'} font-medium`}>
                <LogOut className="w-5 h-5" />
                Log Out
              </button>

              {/* Delete Account Button */}
              {!deletionRequested && (
                <button 
                  data-testid="delete-account-button" 
                  onClick={() => setShowDeletionConfirm(true)} 
                  className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'} font-medium`}
                >
                  <AlertTriangle className="w-5 h-5" />
                  Request Account Deletion
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Deletion Confirmation Modal */}
      {showDeletionConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeletionConfirm(false)} />
          <div className={`relative w-full max-w-sm rounded-2xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-6`}>
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${isDark ? 'bg-red-500/20' : 'bg-red-100'} flex items-center justify-center`}>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${theme.textPrimary}`}>Delete Account?</h3>
              <p className={`text-sm mb-6 ${theme.textMuted}`}>
                This will schedule your account for deletion. You can cancel this request within 30 days. Your chat conversations will be removed immediately for both participants and cannot be restored. Other account data is scheduled for removal after this period.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeletionConfirm(false)} 
                  className={`flex-1 py-3 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'} ${theme.textPrimary} font-medium`}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleRequestDeletion} 
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-medium"
                  data-testid="confirm-deletion-button"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Club Connect Modal */}
      {showClubConnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowClubConnect(false)} />
          <div className={`relative w-full max-w-sm rounded-2xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-6`}>
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${isDark ? 'bg-[#493986]/20' : 'bg-[#493986]/10'} flex items-center justify-center`}>
                <Building2 className="w-8 h-8 text-[#493986]" />
              </div>
              <h3 className={`text-lg font-bold mb-2 ${theme.textPrimary}`}>Connect to Club</h3>
              <p className={`text-sm mb-6 ${theme.textMuted}`}>
                Enter the connect code provided by your club administrator.
              </p>
              <input
                type="text"
                value={connectCode}
                onChange={(e) => setConnectCode(e.target.value.toUpperCase())}
                placeholder="Enter connect code"
                className={`w-full px-4 py-3 rounded-xl border text-center text-xl font-mono tracking-widest mb-4 ${theme.input}`}
                maxLength={8}
                data-testid="club-connect-code-input"
              />
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowClubConnect(false);
                    setConnectCode('');
                  }} 
                  className={`flex-1 py-3 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'} ${theme.textPrimary} font-medium`}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleClubConnect}
                  disabled={connecting || !connectCode.trim()}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium disabled:opacity-50"
                  data-testid="club-connect-submit-button"
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
